import { Button } from '@/components/ui/button';
import { FormModal } from '@/components/ui/form-modal';
import { FullScreenDialog } from '@/components/ui/full-screen-dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  useActualIncidentId,
  useGroupGuid,
} from 'components/IAP/hooks/commonHooks';
import { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';
import { useCreateDraftRoster } from 'components/IAP/hooks/incidentRoster/useCreateDraftRoster';
import { useOptionalCreateRosterDraft } from 'components/IncidentConfig/CreateIncident/CreateRosterDraftContext';
import { useGetIncidentPositionInfo } from 'components/IncidentConfig/hooks/useGetIncidentPositionInfo';
import { useUpdatePositionAndRole } from 'components/IncidentConfig/hooks/useUpdatePositionAndRole';
import { GetIncidentPositionResponse } from 'components/IncidentConfig/incidentConfig.types';
import { ConfirmDeleteDialog } from 'components/Reports/ConfirmDeleteDialog';
import EditRolePermissions, {
  PermissionOption,
} from 'components/Roles/EditRolePermissions';
import { useGetPermissions } from 'components/Roles/hooks/useGetPermissions';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import {
  useDeletePositionForIncident,
  useEditPositionForIncident,
  useGetPositionsForIncident,
  useGetRostersForGroup,
  useGetSeatResourcesForRoster,
  useSetPositionCategory,
  useSetRetireNextOp,
} from 'hooks/incident/useRoster';
import { useCallback, useEffect, useMemo, useState } from 'react';
import PositionAssigneesColumn from './PositionAssigneesColumn';
import {
  membersWithoutLeaderSlots,
  PositionLeaderSlots,
} from './roster/PositionLeaderSlots';
import PositionTasksSection from './PositionTasksSection';
import {
  CUSTOM_POSITION_CATEGORY,
  POSITION_CATEGORIES,
} from './positionCategories';
import { getDescendantIds } from './roster/getDescendantIds';
import PositionPhaseTabs from './roster/PositionPhaseTabs';
import { positionOccupantIds } from './roster/positionPhaseTasks';
import {
  SearchableSelect,
  type SearchableSelectOption,
} from './roster/SearchableSelect';
import { useHwcgFlag } from 'utils/featureFlags';
import { useIncident, usePeriods } from 'components/IAP/hooks/incidentHooks';
import {
  usePlannedOperationalPeriods,
  usePlannedPeriodRosters,
} from 'hooks/incident/usePlannedOperationalPeriods';
import { currentPeriodLabel, plannedPeriodLabel } from './roster/rosterPeriods';
import { PositionPeriodHeader } from './roster/PositionPeriodHeader';

const INCIDENT_PERMISSION_ACTIONS = [
  'incident:changeRoster',
  'incident:proposeRosterChange',
  'incident:approveRosterChange',
  'incident:activateMember',
  'incident:activateSelf',
  'incident:checkInSelf',
  'incident:editCards',
  'incident:generateIap',
  'incident:advanceOperationalPeriod',
  'incident:editSettings',
  'incident:accessAi',
  'incident:viewApprovedMaterials',
  'incident:viewFuturePeriod',
  'incident:roster:change',
  'incident:roster:propose',
  'incident:roster:approve',
  'resourceSet:edit',
  'resourceRequest:create',
  'resourceRequest:signRequestedBy',
  'resourceRequest:signSectionChief',
  'resourceRequest:signResl',
  'resourceRequest:signLogistics',
  'resourceRequest:signFinance',
  'internalLayer:create',
  'internalLayer:propose',
  'internalLayer:approve',
  // Hiding until draft notifications are implemented
  // 'notification:approve',
  // 'notification:propose',
  'sitrep:create',
  'sitrep:draft',
  'sitrep:approve',
  'notification:create',
];

const EditPositionModalHeading: React.FC<{
  positionName: string;
  isTemplatePosition: boolean;
  updatePositionName: (newName: string) => void;
}> = ({ positionName, isTemplatePosition, updatePositionName }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempName, setTempName] = useState(positionName);

  useEffect(() => {
    setTempName(positionName);
  }, [positionName]);

  return (
    <div className="tw:flex tw:items-center tw:gap-2">
      {!isEditing ? (
        <div className="tw:flex tw:items-center tw:gap-2">
          <span>{positionName}</span>
          {isTemplatePosition ? (
            <SharedIcon
              iconName="info"
              size="sm"
              color="primary"
              title={'You cannot edit the name of a template position'}
              aria-label={'You cannot edit the name of a template position'}
              style={{ cursor: 'default' }}
            />
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              onClick={() => setIsEditing(true)}
            >
              <SharedIcon
                iconName="edit"
                size="sm"
                color="primary"
                title={'Edit position name'}
                aria-label={'Edit position name'}
              />
            </Button>
          )}
        </div>
      ) : (
        <div className="tw:flex tw:items-center tw:gap-2">
          <Input
            value={tempName}
            onChange={(e) => setTempName(e.target.value)}
            autoFocus
            className="tw:w-[40vw] tw:max-w-md"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={() => {
              setIsEditing(false);
              updatePositionName(tempName);
            }}
          >
            <SharedIcon
              iconName="check"
              size="sm"
              color="primary"
              title={'Save position name'}
              aria-label={'Save position name'}
            />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={() => {
              setTempName(positionName);
              setIsEditing(false);
            }}
          >
            <SharedIcon
              iconName="close"
              size="sm"
              color="primary"
              title={'Cancel edit'}
              aria-label={'Cancel edit'}
            />
          </Button>
        </div>
      )}
    </div>
  );
};

const RETIRE_NEXT_OP_COPY = {
  available:
    'When on, this position leaves the roster at the start of the next operational period. Assignments from past periods are kept.',
  blockedByDraft:
    'This position has someone or something scheduled on the draft roster for the next operational period. Remove that first.',
  blockedByTemplate: 'Standard ICS positions cannot be retired.',
};

const RetireNextOpToggle: React.FC<{
  positionId: string;
  retireNextOp: boolean;
  isTemplatePosition: boolean;
  hasDraftEntries: boolean;
}> = ({ positionId, retireNextOp, isTemplatePosition, hasDraftEntries }) => {
  const setRetireNextOp = useSetRetireNextOp();
  const [isOn, setIsOn] = useState(retireNextOp);

  useEffect(() => {
    setIsOn(retireNextOp);
  }, [retireNextOp]);

  // Turning the flag off stays available regardless, so a position can always be rescued from a pending retirement.
  const blockedReason = isTemplatePosition
    ? RETIRE_NEXT_OP_COPY.blockedByTemplate
    : hasDraftEntries && !isOn
      ? RETIRE_NEXT_OP_COPY.blockedByDraft
      : null;

  const handleChange = (next: boolean) => {
    setIsOn(next);
    setRetireNextOp.mutate(
      { positionId, retireNextOp: next },
      { onError: () => setIsOn(!next) }
    );
  };

  return (
    <div className="tw:mb-3 tw:flex tw:flex-row tw:items-center tw:justify-between">
      <div className="tw:flex tw:flex-row tw:items-center tw:gap-1">
        <SharedIcon
          iconName="info"
          size="sm"
          color="primary"
          title={blockedReason ?? RETIRE_NEXT_OP_COPY.available}
          aria-label={blockedReason ?? RETIRE_NEXT_OP_COPY.available}
          style={{ cursor: 'default' }}
        />
        <span>Retire Next OP</span>
      </div>
      <Switch
        id={`retire-next-op-${positionId}`}
        checked={isOn}
        disabled={!!blockedReason || setRetireNextOp.isPending}
        onCheckedChange={handleChange}
      />
    </div>
  );
};

const CATEGORY_COPY =
  'The ICS organisational element this seat represents. Choose Custom to name your own.';

const REPORTS_TO_COPY =
  'The position this seat reports to on the org chart. Clear to leave it unattached.';

/**
 * Immediate-commit Reports To control. Self and descendants are excluded so the
 * choice cannot create a cycle; the server re-checks on save.
 */
const PositionReportsToField: React.FC<{
  positionId: string;
  positionName: string;
  isWorkgroup: boolean;
}> = ({ positionId, positionName, isWorkgroup }) => {
  const { data: positions = [] } = useGetPositionsForIncident();
  const editPosition = useEditPositionForIncident();
  const [selected, setSelected] = useState<SearchableSelectOption | null>(null);

  const parentId = useMemo(
    () =>
      positions.find((position) => position.id === positionId)?.parent_id ??
      null,
    [positions, positionId]
  );

  const options = useMemo(() => {
    const ineligible = getDescendantIds(positions, positionId);
    ineligible.add(positionId);

    return positions
      .filter((candidate) => !ineligible.has(candidate.id))
      .map((candidate) => ({ value: candidate.id, label: candidate.name }));
  }, [positions, positionId]);

  useEffect(() => {
    setSelected(options.find((option) => option.value === parentId) ?? null);
  }, [options, parentId]);

  const handleChange = (option: SearchableSelectOption | null) => {
    const previous = selected;
    const nextReportsTo = option ? String(option.value) : null;
    if (nextReportsTo === parentId) {
      setSelected(option);
      return;
    }

    setSelected(option);
    editPosition.mutate(
      {
        id: positionId,
        name: positionName,
        reportsTo: nextReportsTo,
        isWorkgroup,
      },
      { onError: () => setSelected(previous) }
    );
  };

  return (
    <div className="tw:mb-3 tw:flex tw:flex-row tw:items-center tw:justify-between tw:gap-3">
      <div className="tw:flex tw:flex-row tw:items-center tw:gap-1">
        <SharedIcon
          iconName="info"
          size="sm"
          color="primary"
          title={REPORTS_TO_COPY}
          aria-label={REPORTS_TO_COPY}
          style={{ cursor: 'default' }}
        />
        <span>Reports To</span>
      </div>
      <div className="tw:min-w-[240px]">
        <SearchableSelect
          id={`position-reports-to-${positionId}`}
          aria-label="Reports To"
          options={options}
          value={selected}
          onChange={handleChange}
          placeholder="Select a position"
          clearable
          disabled={editPosition.isPending || !positionName}
        />
      </div>
    </div>
  );
};

const REMOVE_FROM_ROSTER_COPY = {
  available:
    'Remove this position from the incident roster. Past assignments are not kept on the chart.',
  blockedByAssignees:
    'Remove all current and scheduled assignees and leaders before removing this position.',
  blockedByResources:
    'Remove attached resources from the active and draft rosters before removing this position.',
  blockedByTemplate:
    'Standard ICS positions cannot be removed; use Retire Next OP',
};

/**
 * Hub-style destructive footer. Only empty custom seats can be deleted;
 * template seats and seats with people or seat-resources stay blocked.
 */
const RemoveFromRosterFooter: React.FC<{
  positionId: string;
  positionName: string;
  isTemplatePosition: boolean;
  hasAssignees: boolean;
  hasSeatResources: boolean;
  onRemoved: () => void;
}> = ({
  positionId,
  positionName,
  isTemplatePosition,
  hasAssignees,
  hasSeatResources,
  onRemoved,
}) => {
  const deletePosition = useDeletePositionForIncident();
  const [showConfirm, setShowConfirm] = useState(false);

  const blockedReason = isTemplatePosition
    ? REMOVE_FROM_ROSTER_COPY.blockedByTemplate
    : hasAssignees
      ? REMOVE_FROM_ROSTER_COPY.blockedByAssignees
      : hasSeatResources
        ? REMOVE_FROM_ROSTER_COPY.blockedByResources
        : null;

  return (
    <>
      <div className="tw:mt-4 tw:border-t tw:border-border tw:pt-3">
        <div className="tw:flex tw:flex-row tw:items-center tw:justify-between tw:gap-3">
          <div>
            <div className="tw:font-semibold">Remove from roster</div>
            <div className="tw:text-sm tw:text-muted-foreground">
              {blockedReason ?? REMOVE_FROM_ROSTER_COPY.available}
            </div>
          </div>
          <Button
            type="button"
            variant="destructive-outline"
            disabled={!!blockedReason || deletePosition.isPending}
            onClick={() => setShowConfirm(true)}
          >
            <SharedIcon iconName="delete" classes="me-1" />
            Remove from roster
          </Button>
        </div>
      </div>

      <ConfirmDeleteDialog
        open={showConfirm}
        title="Remove from roster"
        description={`Are you sure you want to remove ${positionName} from this roster? This cannot be undone.`}
        confirmLabel="Remove from roster"
        pending={deletePosition.isPending}
        onOpenChange={(open) => {
          if (!open) setShowConfirm(false);
        }}
        onConfirm={() => {
          deletePosition.mutate(
            { id: positionId },
            {
              onSuccess: () => {
                setShowConfirm(false);
                onRemoved();
              },
            }
          );
        }}
      />
    </>
  );
};

/**
 * Picks the ICS organisational element a seat represents.
 *
 * This modal has no Save button, so each control commits on its own. The
 * dropdown can save immediately, but the Custom label cannot: the API rejects
 * Custom without a label, so choosing Custom only reveals the input and the
 * save waits until there is something to send. The text then commits on blur or
 * Enter rather than per keystroke.
 */
const PositionCategoryField: React.FC<{
  positionId: string;
  category: string;
  customCategory: string | null;
}> = ({ positionId, category, customCategory }) => {
  const setPositionCategory = useSetPositionCategory();
  const [selected, setSelected] = useState(category);
  const [customLabel, setCustomLabel] = useState(customCategory ?? '');

  useEffect(() => {
    setSelected(category);
    setCustomLabel(customCategory ?? '');
  }, [category, customCategory]);

  const isCustom = selected === CUSTOM_POSITION_CATEGORY;

  const commit = (nextCategory: string, nextLabel: string | null) => {
    setPositionCategory.mutate(
      { positionId, category: nextCategory, customCategory: nextLabel },
      {
        onError: () => {
          setSelected(category);
          setCustomLabel(customCategory ?? '');
        },
      }
    );
  };

  const handleCategoryChange = (next: string | null) => {
    if (!next) return;
    setSelected(next);

    if (next === CUSTOM_POSITION_CATEGORY) {
      // Only worth saving once a label exists; until then the choice is local.
      if (customLabel.trim()) commit(next, customLabel.trim());
      return;
    }

    setCustomLabel('');
    commit(next, null);
  };

  const commitCustomLabel = () => {
    const trimmed = customLabel.trim();
    if (!isCustom || !trimmed) return;
    // Blur fires even when nothing was typed, so skip the request when the label is unchanged.
    if (trimmed === (customCategory ?? '') && category === selected) return;
    commit(CUSTOM_POSITION_CATEGORY, trimmed);
  };

  return (
    <div className="tw:mb-3 tw:flex tw:flex-row tw:items-center tw:justify-between tw:gap-3">
      <div className="tw:flex tw:flex-row tw:items-center tw:gap-1">
        <SharedIcon
          iconName="info"
          size="sm"
          color="primary"
          title={CATEGORY_COPY}
          aria-label={CATEGORY_COPY}
          style={{ cursor: 'default' }}
        />
        <span>Category</span>
      </div>
      <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
        {isCustom && (
          <Input
            aria-label="Custom category label"
            placeholder="Name this category"
            value={customLabel}
            onChange={(e) => setCustomLabel(e.target.value)}
            onBlur={commitCustomLabel}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitCustomLabel();
              }
            }}
          />
        )}
        <Select
          value={selected}
          onValueChange={handleCategoryChange}
          disabled={setPositionCategory.isPending}
        >
          <SelectTrigger
            id={`position-category-${positionId}`}
            className="tw:min-w-[200px]"
            aria-label="Category"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="dark">
            {POSITION_CATEGORIES.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

const EditPositionModal: React.FC<{
  show: boolean;
  onHide: () => void;
  positionId: string;
  activeMembers: RosterEntry[];
  draftMembers: RosterEntry[] | null;
  initialTab?: 'team' | 'planning';
}> = ({
  show,
  onHide,
  positionId,
  activeMembers,
  draftMembers,
  initialTab = 'team',
}) => {
  const resolvedIncidentId = useActualIncidentId();
  const resolvedGroupId = useGroupGuid();
  const draft = useOptionalCreateRosterDraft();
  const isCreateMode = !!draft;
  const structureOnly = !!draft?.structureOnly;
  const allowAssign = !!draft?.allowAssign;
  const hwcg = useHwcgFlag();
  const periodScheduling = hwcg && !isCreateMode;
  const planned = usePlannedOperationalPeriods(
    resolvedIncidentId,
    periodScheduling && show
  );
  const plannedRosters = usePlannedPeriodRosters(
    resolvedIncidentId,
    planned.view.periods,
    periodScheduling && show
  );
  /** `current`, or the id of the planned period being seated. */
  const [seatPeriod, setSeatPeriod] = useState<string>('current');
  const incident = useIncident();
  const periods = usePeriods(periodScheduling && show);
  const currentWindow = periodScheduling
    ? periods.data?.find(
        (period: { id: string }) => period.id === incident.data?.current_period
      )
    : undefined;
  const selectedPlanned =
    seatPeriod === 'current'
      ? null
      : (planned.view.periods.find((period) => period.id === seatPeriod) ??
        null);
  useEffect(() => {
    if (!hwcg || !show) return;
    document.body.classList.add('roster-position-modal-open');
    return () => document.body.classList.remove('roster-position-modal-open');
  }, [hwcg, show]);
  const [positionName, setPositionName] = useState('');
  const [newPermissions, setPositionPermissions] = useState<PermissionOption[]>(
    []
  );
  const [assigneeOp, setAssigneeOp] = useState<'active' | 'draft' | 'both'>(
    'both'
  );
  const [showPermissions, setShowPermissions] = useState(false);

  const updatePositionFromQueries = useCallback(
    (posInfo: GetIncidentPositionResponse) => {
      setPositionName(posInfo.name);
      setPositionPermissions(posInfo.permissions || []);
    },
    [setPositionName, setPositionPermissions]
  );

  const activeForPosition = useMemo(
    () =>
      activeMembers
        .filter((m) =>
          m.incidentPositions?.some((pos) => pos.positionId === positionId)
        )
        .map((m) => ({
          ...m,
          isLeader:
            m.incidentPositions?.find((pos) => pos.positionId === positionId)
              ?.isLeader || false,
        })),
    [activeMembers, positionId]
  );

  const plannedForPosition = useMemo(
    () =>
      selectedPlanned
        ? (plannedRosters.membersByPeriodId[selectedPlanned.id] ?? []).filter(
            (m) =>
              m.incidentPositions?.some((pos) => pos.positionId === positionId)
          )
        : [],
    [plannedRosters.membersByPeriodId, positionId, selectedPlanned]
  );

  const draftForPosition = useMemo(
    () =>
      (draftMembers ?? [])
        .filter((m) =>
          m.incidentPositions?.some((pos) => pos.positionId === positionId)
        )
        .map((m) => ({
          ...m,
          isLeader:
            m.incidentPositions?.find((pos) => pos.positionId === positionId)
              ?.isLeader || false,
        })),
    [draftMembers, positionId]
  );

  const {
    data: positionInfo = {} as GetIncidentPositionResponse,
    isLoading: isLoadingPositionInfo,
    isError: isErrorPositionInfo,
  } = useGetIncidentPositionInfo(
    resolvedIncidentId,
    positionId,
    updatePositionFromQueries
  );

  const {
    data: allPermissions = [],
    isLoading: isLoadingPermissions,
    isError: isErrorPermissions,
  } = useGetPermissions();

  const rostersForGroupQuery = useGetRostersForGroup(resolvedGroupId);

  const { data: draftSeatResources = [] } = useGetSeatResourcesForRoster(true);
  const { data: activeSeatResources = [] } =
    useGetSeatResourcesForRoster(false);

  // A position can only be retired once nothing is waiting to occupy it next period, whether that is a person or an
  // attached resource. The server re-checks this, since the draft can change in another tab.
  const hasDraftEntries = useMemo(
    () =>
      draftForPosition.length > 0 ||
      draftSeatResources.some((res) => res.positionId === positionId),
    [draftForPosition, draftSeatResources, positionId]
  );

  const hasAssignees =
    activeForPosition.length > 0 || draftForPosition.length > 0;

  const hasSeatResources = useMemo(
    () =>
      activeSeatResources.some((res) => res.positionId === positionId) ||
      draftSeatResources.some((res) => res.positionId === positionId),
    [activeSeatResources, draftSeatResources, positionId]
  );

  const updatePositionAndRoleMutation = useUpdatePositionAndRole();

  const createDraftRoster = useCreateDraftRoster();

  const incidentPermissions = useMemo(() => {
    return allPermissions.filter((perm) =>
      INCIDENT_PERMISSION_ACTIONS.includes(perm.action)
    );
  }, [allPermissions]);

  const handlePermissionChange = useCallback(
    (nextPermissions: PermissionOption[]) => {
      setPositionPermissions(nextPermissions);
      updatePositionAndRoleMutation.mutate({
        incidentId: resolvedIncidentId,
        position: {
          id: positionId,
          name: positionName,
        },
        permissions: nextPermissions.map((perm) => ({ id: perm.id })),
      });
    },
    [
      resolvedIncidentId,
      positionId,
      positionName,
      updatePositionAndRoleMutation,
    ]
  );

  const handleNameChange = useCallback(
    (newName: string) => {
      setPositionName(newName);
      updatePositionAndRoleMutation.mutate({
        incidentId: resolvedIncidentId,
        position: {
          id: positionId,
          name: newName,
        },
        permissions: newPermissions.map((perm) => ({ id: perm.id })),
      });
    },
    [
      resolvedIncidentId,
      positionId,
      newPermissions,
      updatePositionAndRoleMutation,
    ]
  );

  const modalHeading = useMemo(() => {
    return (
      <EditPositionModalHeading
        positionName={positionName}
        isTemplatePosition={positionInfo.isTemplate}
        updatePositionName={handleNameChange}
      />
    );
  }, [positionName, positionInfo.isTemplate, handleNameChange]);

  const waitingOnLiveQueries = isCreateMode
    ? isLoadingPositionInfo
    : isLoadingPositionInfo || isLoadingPermissions;
  const liveQueryFailed = isCreateMode
    ? isErrorPositionInfo
    : isErrorPositionInfo || isErrorPermissions;

  // The phase tabs ask what the people in the seat are carrying, so they need
  // the occupants rather than the seat id.
  const occupantIds = useMemo(
    () => positionOccupantIds(activeForPosition, draftForPosition),
    [activeForPosition, draftForPosition]
  );

  /**
   * Everything the modal showed before HWCG. Kept as one node so the flag only
   * decides whether it is wrapped in tabs, and creating a position (which has
   * no roster, tasks or phases yet) always renders it bare.
   */
  const teamTab = (
    <>
      {periodScheduling ? (
        <div className="tw:flex tw:flex-col tw:gap-2">
          <ToggleGroup
            variant="outline"
            size="sm"
            spacing={0}
            multiple={false}
            value={[selectedPlanned ? selectedPlanned.id : 'current']}
            onValueChange={(selected: string[]) => {
              const [next] = selected;
              if (next) setSeatPeriod(next);
            }}
            aria-label="Operational period"
          >
            <ToggleGroupItem
              value="current"
              className="tw:px-2.5 tw:text-xs tw:text-white tw:aria-pressed:bg-sky-700 tw:aria-pressed:text-white"
            >
              {currentPeriodLabel(planned.view.currentPeriodNumber)}
            </ToggleGroupItem>
            {planned.view.periods.map((period) => (
              <ToggleGroupItem
                key={period.id}
                value={period.id}
                className="tw:px-2.5 tw:text-xs tw:text-white tw:aria-pressed:bg-sky-700 tw:aria-pressed:text-white"
              >
                {plannedPeriodLabel(period.periodNumber)}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <PositionPeriodHeader
            incidentId={resolvedIncidentId}
            groupId={resolvedGroupId}
            positionId={positionId}
            label={
              selectedPlanned
                ? plannedPeriodLabel(selectedPlanned.periodNumber)
                : currentPeriodLabel(planned.view.currentPeriodNumber)
            }
            start={
              selectedPlanned
                ? selectedPlanned.startDateTime
                : currentWindow?.start_date_time
            }
            end={
              selectedPlanned
                ? selectedPlanned.anticipatedEndDateTime
                : currentWindow?.anticipated_end_date_time
            }
            members={selectedPlanned ? plannedForPosition : activeForPosition}
            plannedPeriodId={selectedPlanned?.id ?? null}
          />
        </div>
      ) : null}
      <PositionCategoryField
        positionId={positionId}
        category={positionInfo.category ?? 'None'}
        customCategory={positionInfo.customCategory ?? null}
      />
      <PositionReportsToField
        positionId={positionId}
        positionName={positionName}
        isWorkgroup={!!positionInfo.isWorkgroup}
      />
      {!isCreateMode ? (
        <>
          <RetireNextOpToggle
            positionId={positionId}
            retireNextOp={!!positionInfo.retireNextOp}
            isTemplatePosition={positionInfo.isTemplate}
            hasDraftEntries={hasDraftEntries}
          />
          <PositionTasksSection
            positionId={positionId}
            positionName={positionName}
          />
        </>
      ) : null}
      {hwcg && !structureOnly ? (
        <PositionLeaderSlots
          key={selectedPlanned?.id ?? 'current'}
          incidentId={resolvedIncidentId}
          groupId={resolvedGroupId}
          positionId={positionId}
          positionName={positionName}
          members={selectedPlanned ? plannedForPosition : activeForPosition}
          rostersForGroupQuery={rostersForGroupQuery}
          isDraft={false}
          plannedPeriodId={selectedPlanned?.id ?? null}
        />
      ) : null}
      {!structureOnly || allowAssign ? (
        <div className="tw:flex tw:flex-col tw:gap-3">
          {periodScheduling ? null : !isCreateMode ? (
            <ToggleGroup
              variant="outline"
              size="sm"
              spacing={0}
              multiple={false}
              value={[assigneeOp]}
              onValueChange={(selected: string[]) => {
                const [next] = selected;
                if (next) setAssigneeOp(next as 'active' | 'draft' | 'both');
              }}
            >
              <ToggleGroupItem
                value="active"
                className="tw:px-2.5 tw:text-xs tw:text-white tw:aria-pressed:bg-sky-700 tw:aria-pressed:text-white"
              >
                Current OP
              </ToggleGroupItem>
              <ToggleGroupItem
                value="draft"
                className="tw:px-2.5 tw:text-xs tw:text-white tw:aria-pressed:bg-sky-700 tw:aria-pressed:text-white"
              >
                Next OP
              </ToggleGroupItem>
              <ToggleGroupItem
                value="both"
                className="tw:px-2.5 tw:text-xs tw:text-white tw:aria-pressed:bg-sky-700 tw:aria-pressed:text-white"
              >
                Both
              </ToggleGroupItem>
            </ToggleGroup>
          ) : null}
          {periodScheduling ? (
            selectedPlanned ? (
              <PositionAssigneesColumn
                key={selectedPlanned.id}
                incidentId={resolvedIncidentId}
                groupId={resolvedGroupId}
                positionId={positionId}
                positionName={positionName}
                isWorkgroup={positionInfo.isWorkgroup}
                members={membersWithoutLeaderSlots(
                  plannedForPosition,
                  positionId
                )}
                rostersForGroupQuery={rostersForGroupQuery}
                isDraft={false}
                plannedPeriodId={selectedPlanned.id}
                title={`${plannedPeriodLabel(selectedPlanned.periodNumber)} Assignees`}
              />
            ) : (
              <PositionAssigneesColumn
                incidentId={resolvedIncidentId}
                groupId={resolvedGroupId}
                positionId={positionId}
                positionName={positionName}
                isWorkgroup={positionInfo.isWorkgroup}
                members={membersWithoutLeaderSlots(
                  activeForPosition,
                  positionId
                )}
                rostersForGroupQuery={rostersForGroupQuery}
                isDraft={false}
                title="Active Assignees"
              />
            )
          ) : (
            <div
              className={
                isCreateMode || assigneeOp !== 'both'
                  ? undefined
                  : 'tw:grid tw:gap-6 tw:md:grid-cols-2'
              }
            >
              {isCreateMode ||
              assigneeOp === 'active' ||
              assigneeOp === 'both' ? (
                <div
                  className={
                    isCreateMode || assigneeOp !== 'both'
                      ? undefined
                      : 'tw:md:border-r tw:md:border-border tw:md:pr-6'
                  }
                >
                  <PositionAssigneesColumn
                    incidentId={resolvedIncidentId}
                    groupId={resolvedGroupId}
                    positionId={positionId}
                    positionName={positionName}
                    isWorkgroup={positionInfo.isWorkgroup}
                    members={
                      hwcg
                        ? membersWithoutLeaderSlots(
                            activeForPosition,
                            positionId
                          )
                        : activeForPosition
                    }
                    rostersForGroupQuery={rostersForGroupQuery}
                    isDraft={false}
                    title={isCreateMode ? 'Assignees' : 'Active Assignees'}
                    draftMembers={
                      isCreateMode || draftMembers === null
                        ? undefined
                        : draftForPosition
                    }
                  />
                </div>
              ) : null}
              {!isCreateMode &&
              (assigneeOp === 'draft' || assigneeOp === 'both') ? (
                <div>
                  {draftMembers === null ? (
                    <PositionAssigneesColumn
                      incidentId={resolvedIncidentId}
                      groupId={resolvedGroupId}
                      positionId={positionId}
                      positionName={positionName}
                      isWorkgroup={positionInfo.isWorkgroup}
                      members={[]}
                      rostersForGroupQuery={rostersForGroupQuery}
                      isDraft={true}
                      title="Draft Assignees"
                      emptyState={
                        <div className="tw:p-4 tw:text-center">
                          <div className="tw:mb-3 tw:text-muted-foreground">
                            No draft roster yet
                          </div>
                          <Button
                            type="button"
                            onClick={() => createDraftRoster.mutate()}
                            disabled={createDraftRoster.isPending}
                          >
                            {createDraftRoster.isPending
                              ? 'Creating…'
                              : 'Create Draft Roster'}
                          </Button>
                        </div>
                      }
                    />
                  ) : (
                    <PositionAssigneesColumn
                      incidentId={resolvedIncidentId}
                      groupId={resolvedGroupId}
                      positionId={positionId}
                      positionName={positionName}
                      isWorkgroup={positionInfo.isWorkgroup}
                      members={
                        hwcg
                          ? membersWithoutLeaderSlots(
                              draftForPosition,
                              positionId
                            )
                          : draftForPosition
                      }
                      rostersForGroupQuery={rostersForGroupQuery}
                      isDraft={true}
                      title="Draft Assignees"
                    />
                  )}
                </div>
              ) : null}
            </div>
          )}
        </div>
      ) : null}
      {!isCreateMode ? (
        <div className="tw:flex tw:flex-col tw:gap-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="tw:w-fit"
            onClick={() => setShowPermissions((current) => !current)}
          >
            {showPermissions ? 'Hide Permissions' : 'Show Permissions'}
          </Button>
          {showPermissions ? (
            <EditRolePermissions
              permissionsOptions={incidentPermissions}
              currentPermissions={newPermissions}
              onPermissionChange={handlePermissionChange}
            />
          ) : null}
        </div>
      ) : null}
      <RemoveFromRosterFooter
        positionId={positionId}
        positionName={positionName}
        isTemplatePosition={positionInfo.isTemplate}
        hasAssignees={hasAssignees}
        hasSeatResources={hasSeatResources}
        onRemoved={onHide}
      />
    </>
  );

  const phased = hwcg && !structureOnly;

  const shellProps = {
    open: show,
    onOpenChange: (open: boolean) => {
      if (!open) onHide();
    },
    title:
      waitingOnLiveQueries || liveQueryFailed ? 'Edit Position' : modalHeading,
  };

  const body = waitingOnLiveQueries ? (
    <div>Loading...</div>
  ) : liveQueryFailed ? (
    <div>Error loading position info</div>
  ) : phased ? (
    <PositionPhaseTabs
      team={teamTab}
      occupantIds={occupantIds}
      positionId={positionId}
      positionName={positionName}
      incidentId={resolvedIncidentId}
      initialTab={initialTab}
      pendingIncident={isCreateMode}
    />
  ) : (
    teamTab
  );

  /**
   * Six tabs of roster, phases and assets need the whole viewport; 80vw of a
   * centered dialog capped at 85vh left the task lists scrolling inside a
   * scroll. Template editors keep the smaller sheet, having no tabs.
   */
  return phased ? (
    <FullScreenDialog {...shellProps}>{body}</FullScreenDialog>
  ) : (
    <FormModal
      {...shellProps}
      widthClassName="tw:!max-w-[80vw] tw:sm:!max-w-[80vw]"
    >
      {body}
    </FormModal>
  );
};

export default EditPositionModal;
